import React, { useState, SyntheticEvent, ReactElement, useContext } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import { HttpClient } from '../../api/HttpClient';
import { useAlert } from '../../contexts/alert/useAlert';
import { LoadingButton } from '../../components/LoadingButton';
import { InputValidatorMode, useUserNameValidator } from '../../contexts/useInputValidator';
import { RetroRoomResponse } from '../../api/Requests';
import { Session, SessionStorage } from '../../localstorage/SessionStorage';
import { ContextPlayer, PlayerContext } from '../../contexts/PlayerContext';
import { UserContext } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';
import { useTracking } from '../../analytics/useTracking';
import { RetroBetaBanner } from './RetroBetaBanner';
// PlayerContext is used via useContext below

export default function CreateRetroRoom(): ReactElement {
  const [validateUserName] = useUserNameValidator();
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const alert = useAlert();
  const { setPlayer } = useContext(PlayerContext);
  const { authToken } = useContext(UserContext);
  const { track } = useTracking();
  const navigate = useNavigate();
  const sessionStorage = new SessionStorage();

  function validateInput(value: string, mode: InputValidatorMode) {
    const valid = validateUserName(value, mode).isValid;
    setIsValid(valid);
    return valid;
  }

  function handleOnChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUserName(event.target.value);
    validateInput(event.target.value, InputValidatorMode.onTyping);
  }

  async function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    if (!validateInput(userName, InputValidatorMode.onSubmit)) return;

    setIsLoading(true);
    const httpClient = new HttpClient(authToken);
    try {
      const response = await httpClient.post('/retro-rooms', { playerName: userName }) as RetroRoomResponse;
      const player = response.players.find(p => p.name === userName);
      if (player) {
        track.event(RetroCategory.Room, RetroAction.RoomCreated);
        sessionStorage.SaveSession(new Session(response.urlId, player.id, userName));
        setPlayer(new ContextPlayer(player.id, userName, true));
        navigate(`/retro/${response.urlId}`);
      } else {
        setIsLoading(false);
        alert.showMessage('An unexpected error occurred, please try again', 'error');
      }
    } catch {
      setIsLoading(false);
      alert.showMessage('Not able to create retro room, please try again', 'error');
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container sx={{ padding: 3 }} spacing={3} direction="column" justifyContent="space-between" alignItems="center">
        <Grid>
          <Typography align="center" variant="h5">Create a retrospective room</Typography>
          <Typography align="center" variant="subtitle1">and collaborate with sticky notes in real time</Typography>
        </Grid>
        <Grid>
          <TextField
            autoComplete="nickname"
            error={!isValid}
            helperText={validateUserName(userName, InputValidatorMode.onTyping).errorMessage}
            disabled={isLoading}
            onChange={handleOnChange}
            label="Please enter your name"
            variant="outlined"
            color="secondary"
          />
        </Grid>
        <Grid>
          <LoadingButton type="submit" isLoading={isLoading}>Create Retro Room</LoadingButton>
        </Grid>
        <Grid sx={{ width: '100%', maxWidth: 480 }}>
          <RetroBetaBanner />
        </Grid>
      </Grid>
    </form>
  );
}
